/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect } from 'react';
import SkillStats from '../model/SkillStats';
import ItemOption from '../model/ItemOption';

interface ItemSectionProps {
    racialSelected: string[];
    racialOptions: ItemOption[];
    onRacialSelectedChanged: (values: string[]) => void;
    classSelected: string[];
    classOptions: ItemOption[];
    onClassSelectedChanged: (values: string[]) => void;
    backgroundSelected: string[];
    backgroundOptions: ItemOption[];
    onBackgroundSelectedChanged: (values: string[]) => void;
    possibleSet: string[] | null;
    name: string;
}

const ItemSection: React.FC<ItemSectionProps> = ({
    racialSelected,
    racialOptions,
    onRacialSelectedChanged,
    classSelected,
    classOptions,
    onClassSelectedChanged,
    backgroundSelected,
    backgroundOptions,
    onBackgroundSelectedChanged,
    possibleSet,
    name
}) => {

    // Handle change in select box
    const handleSelectChange = (value: string, index: number, list: string[], changeCallBack: (values: string[]) => void) => {
        const updatedValues = [...list];
        updatedValues[index] = value;
        changeCallBack(updatedValues);
    };

    const renderItem = (items: ItemOption, index: number, id: string, valueArray: string[], selectedChangedCallBack: (values: string[]) => void) => {
        let availableItems:string[] = [];
        if (possibleSet == null) {
            possibleSet = [...new Set([...racialOptions, ...classOptions, ...backgroundOptions].map(o=>o.options).flat())];
        }
        availableItems = [...possibleSet, ...possibleSet.map(o => o + SkillStats.EXPERT)];

        let options = items.options?.filter(opt => availableItems.includes(opt)) || [];
        
        // remove duplicates:
        options = [...new Set(options)];
        
        // for any item marked as expertise, make sure they have the base skill selected already
        options = options.filter(opt => {
            if (!opt.endsWith(SkillStats.EXPERT)) {
                return true;
            }
            const base = opt.split(SkillStats.EXPERT)[0];
            for (const item of [racialSelected, classSelected, backgroundSelected]) {
                if (item) {
                    for (let i = 0; i < item.length; i++) {
                        if ((i !== index || item !== valueArray) && (item[i] === base)) {
                            return true;
                        }
                    }
                }
            }
            return false;
        });
        if (options.length === 1) {
            if (valueArray[index] !== options[0]) {
                const modifiedValueArray = [...valueArray];
                modifiedValueArray[index] = options[0];
                
                // Defer the callback so it runs after render
                setTimeout(() => selectedChangedCallBack(modifiedValueArray), 0);
            }
        } else if (options.length === 0) {
            options = [...availableItems];
        }

        const value = valueArray[index] || '';
        let usageCount = 0;
        for (const options of [racialSelected, classSelected, backgroundSelected]) {
            usageCount += options.filter(o => o === value).length;
        }
        const className = (value === '' || value === 'Any' || usageCount > 1) ? ' duplicate' : '';

        return (
            <tr key={index + id} className={className}>
                <td className='no-wrap'>
                    {items.label}:
                </td>
                <td>
                    <select value={value}
                        style={{ width: 'auto', minWidth: '100px', margin: '2px', maxWidth: '300px' }}
                        disabled={options.length === 1}
                        title={value}
                        onChange={(e) => handleSelectChange(e.target.value, index, valueArray, selectedChangedCallBack)}>
                        <option value=''>-- Select --</option>
                        {
                            options.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))
                        }
                    </select>
                </td>
            </tr>
        );
    };

    // Render select boxes based on the current number of specified boxes
    const renderItems = () => {
        return (
            <>
                <div style={{ textAlign: 'center', width: '100%', fontWeight: '600', textDecoration: 'underline' }}>{name}</div>
                <table>
                    <tbody>
                        {racialOptions?.map(    (value, index) => renderItem(value, index, 'r', racialSelected,     onRacialSelectedChanged))}
                        {classOptions?.map(     (value, index) => renderItem(value, index, 'c', classSelected,      onClassSelectedChanged))}
                        {backgroundOptions?.map((value, index) => renderItem(value, index, 'b', backgroundSelected, onBackgroundSelectedChanged))}
                    </tbody>
                </table>
            </>
        );
    };

    return renderItems();
};

export default ItemSection;
